import type { SdkEnvelope, SdkErrorBody, SdkEventMessage, SdkRequestMessage, SdkResponseMessage } from "#envelope";
import { isSdkEnvelope } from "#envelope";
import { sdkEnvelopeSchema } from "#schema";
import { PROTOCOL_VERSION } from "#version";

export function createEventMessage<TType extends string, TPayload>(type: TType, payload: TPayload): SdkEventMessage<TType, TPayload> {
	return {
		v: PROTOCOL_VERSION,
		kind: "event",
		type,
		payload,
	};
}

export function createRequestMessage<TType extends string, TPayload>(type: TType, id: string, payload: TPayload): SdkRequestMessage<TType, TPayload> {
	return {
		v: PROTOCOL_VERSION,
		kind: "request",
		type,
		id,
		payload,
	};
}

export function createResponseOkMessage<TType extends string, TPayload>(type: TType, id: string, payload: TPayload): SdkResponseMessage<TType, TPayload> {
	return {
		v: PROTOCOL_VERSION,
		kind: "response",
		type,
		id,
		ok: true,
		payload,
	};
}

export function createResponseErrMessage<TType extends string>(type: TType, id: string, error: SdkErrorBody): SdkResponseMessage<TType> {
	return {
		v: PROTOCOL_VERSION,
		kind: "response",
		type,
		id,
		ok: false,
		error,
	};
}

/** Wire format: JSON string (TWA CustomTabsSession / many WebView bridges accept String). */
export function serializeSdkEnvelope(message: SdkEnvelope): string {
	return JSON.stringify(message);
}

/**
 * Parse a wire payload (JSON string or already-parsed object).
 * Returns `null` if the value is not a valid v1 envelope.
 */
export function parseSdkEnvelope(raw: unknown): SdkEnvelope | null {
	let value: unknown = raw;

	if (typeof raw === "string") {
		try {
			value = JSON.parse(raw);
		} catch {
			return null;
		}
	}

	const parsed = sdkEnvelopeSchema.safeParse(value);
	if (parsed.success) {
		return parsed.data as SdkEnvelope;
	}

	// Fast path without zod allocation details — keep both in sync via tests later
	return isSdkEnvelope(value) ? value : null;
}

export function createRequestId(): string {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
		return crypto.randomUUID();
	}
	return `sdk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
